import React, { useEffect, useState } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../services/firebase';
import { exchangeGuidedAccessToken, peekGuidedAccessToken } from '../services/guidedClientService';
import { getTranslation } from '../utils/translations';
import { writeGuidedSession } from '../utils/accountMode';
import type { Language } from '../types';

interface GuidedAccessLoginProps {
    accessToken: string;
    language?: Language;
    onSuccess: () => void;
    onUseStandardLogin: () => void;
}

const GuidedAccessLogin: React.FC<GuidedAccessLoginProps> = ({
    accessToken,
    language = 'he',
    onSuccess,
    onUseStandardLogin,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [email, setEmail] = useState('');
    const [linkValid, setLinkValid] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        peekGuidedAccessToken(accessToken)
            .then(() => {
                if (!cancelled) setLinkValid(true);
            })
            .catch(() => {
                if (!cancelled) {
                    setLinkValid(false);
                    setError(t('guidedAccessInvalidLink'));
                }
            });
        return () => { cancelled = true; };
    }, [accessToken, language]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const result = await exchangeGuidedAccessToken(accessToken, email.trim());
            await signInWithCustomToken(auth, result.customToken);
            writeGuidedSession({
                email: email.trim().toLowerCase(),
                clientId: result.clientId,
                consultantId: result.consultantId,
                householdId: result.householdId,
            });
            window.history.replaceState({}, '', '/');
            onSuccess();
        } catch (err) {
            const message = err instanceof Error ? err.message : '';
            if (message.includes('match') || message.includes('deactivated') || message.includes('expired') || message.includes('invalid')) {
                setError(message.includes('match') ? t('guidedAccessEmailMismatch') : t('guidedAccessInvalidLink'));
            } else {
                setError(t('guidedAccessFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
            <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 p-8">
                <h1 className="font-nunito font-black text-3xl text-brand-sage tracking-tighter mb-2">mealPai</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{t('guidedAccessSubtitle')}</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">
                            {t('email')}
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 dir-ltr"
                            placeholder={t('guidedAccessEmailPlaceholder')}
                        />
                    </div>

                    {error && (
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium">{error}</p>
                    )}

                    <button
                        type="submit"
                        disabled={loading || !email.trim() || linkValid === false}
                        className="w-full py-3 bg-brand-sage text-white font-black rounded-xl disabled:opacity-50"
                    >
                        {loading ? t('loading') : t('guidedAccessContinue')}
                    </button>
                </form>

                <button
                    type="button"
                    onClick={onUseStandardLogin}
                    className="w-full mt-4 text-sm text-slate-500 hover:text-brand-sage font-bold"
                >
                    {t('guidedAccessStandardLogin')}
                </button>
            </div>
        </div>
    );
};

export default GuidedAccessLogin;
